<script setup>
    import { defineProps } from 'vue'
    import { CheckCircleIcon, ExclamationIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/vue/solid'

    const props = defineProps({ item: { type: Object, required: true } })

    const listIcons = {
        alert: ExclamationIcon,
        warning: ExclamationIcon,
        success: CheckCircleIcon,
        error: XCircleIcon,
        info: InformationCircleIcon,
    }

    const icon = (icon) => listIcons[icon] || InformationCircleIcon
</script>

<template>
    <div
        aria-live="assertive"
        class="w-80 flex items-end px-6 pb-6 pointer-events-none sm:items-start"
    >
        <div class="w-full flex flex-col items-center space-y-4 sm:items-end">
            <div
                class="max-w-sm w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
                :class="['notification', props.item.type]"
            >
                <div class="p-4">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <Component
                                :is="icon(props.item.type)"
                                class="h-6 w-6 notification_icon"
                                aria-hidden="true"
                            />
                        </div>

                        <div class="ml-3 w-0 flex-1 pt-0.5">
                            <p
                                v-if="props.item?.title"
                                class="text-sm font-medium notification_title"
                            >
                                {{ props.item?.title }}
                            </p>

                            <p
                                v-if="props.item.text"
                                class="mt-1 text-sm notification_text"
                                v-html="props.item.text"
                            />
                        </div>

                        <div class="ml-4 flex-shrink-0 flex">
                            <button
                                class="rounded-md chat_transition inline-flex notification_button focus:outline-none focus:ring-2 focus:ring-offset-2"
                                @click="$notify.close(props.item.id)"
                            >
                                <span class="sr-only">Close</span>

                                <XCircleIcon
                                    name=""
                                    class="h-5 w-5"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style
    type="scss"
    scoped
>
.notification.error {
    @apply bg-red-50 shadow-red-200/30;
}

.notification.error .notification_title {
    @apply text-red-800;
}

.notification.error .notification_text {
    @apply text-red-700;
}

.notification.error .notification_icon {
    @apply text-red-400;
}

.notification.error .notification_button {
    @apply text-red-500 hover:text-red-700 focus:ring-offset-red-50 focus:ring-red-600
}

.notification.success {
    @apply bg-green-50 shadow-green-200/30;
}

.notification.success .notification_title {
    @apply text-green-800;
}

.notification.success .notification_text {
    @apply text-green-700;
}

.notification.success .notification_icon {
    @apply text-green-400;
}

.notification.success .notification_button {
    @apply text-green-500 hover:text-green-700 focus:ring-offset-green-50 focus:ring-green-600
}

.notification.warning {
    @apply bg-yellow-50 shadow-yellow-200/30;
}

.notification.warning .notification_title {
    @apply text-yellow-800;
}

.notification.warning .notification_text {
    @apply text-yellow-700;
}

.notification.warning .notification_icon {
    @apply text-yellow-400;
}

.notification.warning .notification_button {
    @apply text-yellow-500 hover:text-yellow-700 focus:ring-offset-yellow-50 focus:ring-yellow-600
}

.notification.infor {
    @apply bg-blue-50 shadow-blue-200/30;
}

.notification.infor .notification_title {
    @apply text-blue-800;
}

.notification.infor .notification_text {
    @apply text-blue-700;
}

.notification.infor .notification_icon {
    @apply text-blue-400;
}

.notification.infor .notification_button {
    @apply text-blue-500 hover:text-blue-700 focus:ring-offset-blue-50 focus:ring-blue-600
}
</style>
